import React, { useEffect, useMemo, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { APP_TOKEN_KEY, ICON_SIZES } from "../../helpers/constants";
import ModalsLoader from "../Modals/ModalsLoader";
import clsx from "clsx";
import useAllTransactions from "src/hooks/useAllTransactions";
import useCurrentUser from "src/hooks/useCurrentUser";
import LoginFlowLogo from "../LoginFlowLogo/LoginFlowLogo";
import useCurrentAccount from "src/hooks/useCurrentAccount";
import useMainFilter from "src/hooks/useMainFilter";
import useFilteredTransactions from "src/hooks/useFilteredTransactions";
import toast from "react-hot-toast";
import useMobile from "src/hooks/useMobile";
import useAccountLocalStorage from "src/hooks/useAccountLocalStorage";
import userPreferencesStore from "src/store/userPreferences";
import useDarkMode from "src/hooks/useDarkMode";
import ScrollUpDown from "../ScrollUpDown/ScrollUpDown";
import { MdHub } from "react-icons/md";
import { useTranslation } from "react-i18next";

const ProtectedRoutes: React.FC = () => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  const location = useLocation();
  const { t } = useTranslation();
  const { updateTheme } = useDarkMode();

  const {
    isLoadingAllTransactions,
    isLoadingCustomizedCategories,
    isLoadingCustomizedSources,
    isRefetchingAllTransactions,
    isRefetchingCustomizedCategories,
    isRefetchingCustomizedSources,
  } = useAllTransactions();
  const { isRefetchingFilteredTransactions } = useFilteredTransactions();
  const { isLoadingAccounts } = useCurrentUser();
  const { isLoadingCurrentAccount } = useCurrentAccount();
  const { mainFilter, setMainFilter } = useMainFilter();
  const { isMobile } = useMobile();
  const { accountLocalStoragePreferences } = useAccountLocalStorage();
  const { setSelectedYear } = userPreferencesStore();
  const toastIdRef = useRef<string | undefined>(undefined);

  const isLoadingSomething = useMemo(() => {
    return [
      !!isLoadingAccounts,
      !!isLoadingAllTransactions,
      !!isLoadingCustomizedCategories,
      !!isLoadingCustomizedSources,
      !!(mainFilter?.account ? isLoadingCurrentAccount : false),
    ].includes(true);
  }, [
    isLoadingAccounts,
    isLoadingAllTransactions,
    isLoadingCustomizedCategories,
    isLoadingCustomizedSources,
    mainFilter?.account,
    isLoadingCurrentAccount,
  ]);

  const isRefetchingSomething = useMemo(() => {
    return [
      isRefetchingAllTransactions,
      isRefetchingCustomizedCategories,
      isRefetchingCustomizedSources,
      isRefetchingFilteredTransactions,
    ].includes(true);
  }, [
    isRefetchingAllTransactions,
    isRefetchingCustomizedCategories,
    isRefetchingCustomizedSources,
    isRefetchingFilteredTransactions,
  ]);

  useEffect(() => {
    const now = Date.now();

    if (isRefetchingSomething && !toastIdRef.current) {
      toastIdRef.current = toast.loading(t("misc.syncing"), {
        position: isMobile ? "bottom-center" : "bottom-right",
        duration: 3000,

        icon: (
          <MdHub
            size={ICON_SIZES.XS}
            className={clsx([
              "animate-spin",
              "text-red-400",
              "dark:text-purple-400",
            ])}
          />
        ),
      });
    }

    if (!isRefetchingSomething && toastIdRef.current) {
      const timeout = setTimeout(() => {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = undefined;
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [isRefetchingSomething]);

  useEffect(() => {
    if (!isLoadingSomething) {
      setMainFilter({ account: accountLocalStoragePreferences?.lastAccount });
      setSelectedYear(accountLocalStoragePreferences?.lastYear);
    }
  }, [isLoadingSomething]);

  useEffect(() => {
    updateTheme();
  }, []);

  return token ? (
    <>
      {isLoadingSomething ? (
        <div
          className={clsx([
            "flex",
            "justify-center",
            "flex-col",
            "gap-0",
            "w-full",
            "items-center",
            "flex-grow",
            "h-[100vh]",
          ])}
        >
          <LoginFlowLogo />
          <div
            className={clsx([
              "text-xs",
              "dark:text-cyan-400",
              "text-gray-400",
              "md:text-sm",
              "font-normal",
              "hidden",
            ])}
          >
            by CALIROJAS DEV.
          </div>
        </div>
      ) : (
        <>
          <ModalsLoader />
          <Outlet />
          <ScrollUpDown />
        </>
      )}
    </>
  ) : (
    <Navigate to="/" state={{ from: location }} replace />
  );
};

export default ProtectedRoutes;
